import {
  SYSTEM_DATA_TYPES,
  PatternOptions,
  PolicyParametersContext,
  PolicyParametersData,
  PolicySettingsRouteParams
} from '../types'
import { getUpdatedUrl } from '../../../../utils/urlUtil'
import { BASE_PATH } from '../constants'
import { EDIT, URL_CREATE, URL_POLICY_PARAMETERS } from '../../../../constants'
import historyService from '../../../../services/historyService'
import { isArrayEmptyOrFalsy } from '../../../../utils/common'
import useUrlContext from '../../../../hooks/useUrlContext'
import { useReduxSelector } from '../../../../hooks/useReduxSelector'
import { createPatcher, hasDuplicates } from '../../../../utils'
import { resetValidationResult } from '../../../attributes/attributesSlice'
import { validateString } from '../../../attributes/settings/utils'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'

const PolicyParametersContext = React.createContext<PolicyParametersContext | null>(null)

const initialContext: PolicyParametersData = {
  basicDetails: {
    name: '',
    enabled: false,
    dataType: SYSTEM_DATA_TYPES.DATE
  },

  detection: {
    detectionType: PatternOptions.BASIC,
    regexValues: [''],
    regexPatternTypes: [SYSTEM_DATA_TYPES.DATE],
    contextWords: ['']
  }
}

const PolicyParametersContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [basicDetails, setBasicDetails] = useState(initialContext.basicDetails)
  const context = useUrlContext({ pageName: URL_POLICY_PARAMETERS })
  const dispatch = useDispatch()
  const isEdit = context.queryParams[EDIT] === 'true'
  const params = useParams<PolicySettingsRouteParams>()
  const { alertSubPage } = params
  const alertSubPageWithSlash = `/${alertSubPage}`

  const [detection, setDetection] = useState(initialContext.detection)
  const [testText, setTestText] = useState('')
  const isCreate = alertSubPageWithSlash === URL_CREATE
  const isView = !isCreate && alertSubPage && !isEdit

  const policyParamIdFromUrl = isCreate ? '' : alertSubPage

  const mode = {
    isCreate,
    isEdit,
    isView
  }

  const selectedPolicyParameter = useReduxSelector(
    (state) => state.policySettings.policyParamerters.selectedPolicyParameter
  )

  useEffect(
    function setContextDataWithSelectedPolicyParameter() {
      if (selectedPolicyParameter && policyParamIdFromUrl) {
        patchBasicDetails({
          name: selectedPolicyParameter.name,
          description: selectedPolicyParameter.description,
          dataType: selectedPolicyParameter.dataType,
          enabled: selectedPolicyParameter.enabled
        })
        const detectionType = selectedPolicyParameter.regexPatternTypes
          ? PatternOptions.BASIC
          : PatternOptions.ADVANCED
        patchDetection({
          detectionType,
          regexValues: selectedPolicyParameter.patterns?.map((pattern) => pattern.regex) || [''],
          regexPatternTypes: selectedPolicyParameter.regexPatternTypes,
          contextWords: selectedPolicyParameter.context?.map((context) => context?.keyword)
        })
      }
    },
    [policyParamIdFromUrl, selectedPolicyParameter]
  )

  function handleDetectionTypeChange(detectionType: PatternOptions) {
    setDetection({
      ...initialContext.detection,
      detectionType: detectionType,
      contextWords: detection.contextWords
    })
  }

  const navigateToCreateOrEditParameter = (id?: string, forEdit?: boolean) => {
    const url = getUpdatedUrl({
      pathName: BASE_PATH + URL_POLICY_PARAMETERS + (id ? `/${id}` : URL_CREATE),
      ...(forEdit
        ? {
            pageName: URL_POLICY_PARAMETERS,
            queryParams: {
              [EDIT]: forEdit ? 'true' : 'false'
            }
          }
        : {})
    })
    historyService.push(url)
  }

  const patchDetection = useMemo(() => createPatcher(setDetection), [])
  const patchBasicDetails = useMemo(() => createPatcher(setBasicDetails), [])

  const isFormValid = useMemo(() => {
    return !!(
      basicDetails.name &&
      basicDetails.dataType &&
      detection.detectionType &&
      (detection.detectionType === PatternOptions.ADVANCED
        ? !isArrayEmptyOrFalsy(detection.regexValues)
        : !isArrayEmptyOrFalsy(detection.regexPatternTypes)) &&
      !isArrayEmptyOrFalsy(detection.contextWords) &&
      detection.contextWords.every((val) => validateString(val, 3, 100).valid) &&
      !hasDuplicates(detection.contextWords)
    )
  }, [basicDetails, detection])

  const resetForm = useCallback(() => {
    setBasicDetails(initialContext.basicDetails)
    setDetection(initialContext.detection)
    setTestText('')
    dispatch(resetValidationResult())
  }, [setBasicDetails, setDetection])

  useEffect(() => {
    dispatch(resetValidationResult())
  }, [testText, detection.contextWords, detection.regexValues])

  return (
    <PolicyParametersContext.Provider
      value={{
        basicDetails,
        patchBasicDetails,
        detection,
        patchDetection,
        mode,
        navigateToCreateOrEditParameter,
        isFormValid,
        resetForm,
        testText,
        setTestText,
        policyParamIdFromUrl,
        handleDetectionTypeChange
      }}
    >
      {children}
    </PolicyParametersContext.Provider>
  )
}

export const usePolicyParametersContext = () => {
  const context = React.useContext(PolicyParametersContext)
  if (!context) {
    throw new Error(
      'usePolicyParametersContext must be used within a PolicyParametersContextProvider'
    )
  }
  return context
}

export default PolicyParametersContextProvider
